import { create } from 'zustand';
import type {
  GameState, GamePhase, TurnPhase, Player, BoardCell,
  PropertyOwnership, GameLog, ReverseCardId,
  FateCard, ActiveGlobalEvent, PlayerSnapshot
} from '../types';
import { BOARD_CELLS, CHARACTERS, IDENTITIES, FATE_CARDS, REVERSE_CARDS } from '../data';
import { rollDice } from '../logic/diceEngine';
import { applyFateCard, applyReverseCard, shouldTriggerGlobalEvent, forceTriggerGlobalEvent, getGlobalEvent, applyGlobalEvent, resetGlobalEventTimer } from '../logic/gameEngine';
import { addToast } from '../components/Toast/ToastStack';
import { shuffle } from '../utils/shuffle';

// --- 初始状态 ---
const initialBoard = BOARD_CELLS;

function createInitialState(): GameState {
  return {
    phase: 'lobby',
    turnPhase: 'waiting',
    players: [],
    currentPlayerIndex: 0,
    turnNumber: 0,
    roundNumber: 0,
    board: initialBoard,
    properties: [],
    activeGlobalEvent: null,
    auction: null,
    logs: [],
    winnerId: null,
    diceResult: null,
    turnCounter: 0,
    lastComeback: 0,
    comebackNotification: null as { id: number; emoji: string; title: string; description: string; } | null,
    landChangeNotification: null as { id: number; cellId: number; cellName: string; landType: 'lost' | 'downgraded' | 'stolen' | 'swapped' | 'bankrupt'; message: string; } | null,

    // 全局事件广播（所有玩家可见）
    broadcast: null as { message: string; type: 'info' | 'success' | 'warning' | 'error' } | null,

    // 破产清算
    showBankruptcyLiquidation: false,
    pendingBankruptPlayerId: null as string | null,
  };
}

// --- Store Actions ---
interface GameActions {
  // 初始化
  initGame: (playerCount: number) => void;
  addPlayer: (name: string, characterId: string, isBot?: boolean) => void;
  selectCharacter: (playerId: string, characterId: string) => void;
  assignIdentities: () => void;
  startGame: () => void;
  resetGame: () => void;

  // 回合控制
  setTurnPhase: (phase: TurnPhase) => void;
  nextTurn: () => void;
  nextPlayer: () => void;

  // 骰子
  rollDiceAction: () => [number, number];
  movePlayer: (playerId: string, steps: number) => number;

  // 地产
  buyProperty: (playerId: string, cellId: number) => boolean;
  upgradeProperty: (playerId: string, cellId: number) => boolean;
  payRent: (fromId: string, toId: string, cellId: number) => number;

  // 卡牌
  addReverseCard: (playerId: string, cardId: ReverseCardId) => void;
  removeReverseCard: (playerId: string, cardId: ReverseCardId) => void;
  drawFateCard: (playerId: string) => FateCard | null;
  shuffleFateCards: () => void;

  // 身份
  revealIdentity: (playerId: string) => void;

  // 经济
  addMoney: (playerId: string, amount: number) => void;
  deductMoney: (playerId: string, amount: number) => number;
  payTax: (playerId: string) => number;

  // 监狱
  sendToJail: (playerId: string) => void;
  payBail: (playerId: string) => void;
  freeFromJail: (playerId: string) => void;
  waitInJail: (playerId: string) => void;

  // 全局事件
  checkAndTriggerGlobalEvent: (force?: boolean, silent?: boolean) => ActiveGlobalEvent | null;

  // 破产
  checkBankruptcy: (playerId: string) => boolean;
  handleBankruptcy: (playerId: string) => void;
  // 破产清算
  startBankruptcyLiquidation: (playerId: string) => void;
  cancelBankruptcyLiquidation: () => void;
  confirmBankruptcy: () => void;
  sellPropertyForLiquidation: (playerId: string, cellId: number) => void;

  // 绝境反击
  checkComeback: (playerId: string) => void;

  // 地产变动通知
  notifyLandChange: (cellId: number, landType: 'lost' | 'downgraded' | 'stolen' | 'swapped' | 'bankrupt', extraMessage?: string) => void;

  // 日志
  addLog: (playerId: string, message: string, type: GameLog['type']) => void;

  // 全局广播
  setBroadcast: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void;
  clearBroadcast: () => void;

  // 快捷操作
  getCurrentPlayer: () => Player;
  getCellById: (cellId: number) => BoardCell;
  getPlayerCell: (playerId: string) => BoardCell;
  setDiceResult: (result: [number, number] | null) => void;
  setPhase: (phase: GamePhase) => void;
  setWinner: (winnerId: string) => void;

  // 角色技能
  getFateDeckPreview: () => FateCard[];
  chooseDiceSteps: (steps: number) => void;
  owlPreviewFate: () => FateCard | null;

  // 内部状态
  _fateDeck: FateCard[];
  _chosenSteps: number | null; // 兔子赌徒：玩家选择的步数
  _diceOptionHigh: number | null; // 兔子：骰子高值
  _diceOptionLow: number | null;  // 兔子：骰子低值
  _restoreState: (playerId: string) => void;
}

export const useGameStore = create<GameState & GameActions>((set, get) => ({
  ...createInitialState(),
  _fateDeck: [],
  _chosenSteps: null as number | null,
  _diceOptionHigh: null as number | null,
  _diceOptionLow: null as number | null,

  initGame: (playerCount: number) => {
    const state = createInitialState();
    // 洗命运卡
    const shuffled = shuffle(FATE_CARDS);
    // 重置全局事件计时器（防止跨局泄漏）
    resetGlobalEventTimer();
    set({ ...state, _fateDeck: shuffled });
  },

  addPlayer: (name, characterId, isBot = false) => {
    const state = get();
    const char = CHARACTERS.find(c => c.id === characterId);
    if (!char) return;

    const newPlayer: Player = {
      id: `player_${state.players.length + 1}`,
      name: name || `玩家${state.players.length + 1}`,
      characterId,
      identityId: '',
      identityRevealed: false,
      money: 1500,
      position: 0,
      previousPosition: 0,
      inJail: false,
      jailTurns: 0,
      properties: [],
      reverseCards: [],
      fateCardHistory: [],
      skippedTurns: 0,
      skipNextTurn: false,
      immuneTurns: 0,
      incomeHalved: false,
      reverseWalk: false,
      allianceWith: null,
      allianceTurns: 0,
      lastDice: null,
      preciseSteps: null,
      consecutiveNoIncome: 0,
      isBot,
      bankrupt: false,
    };

    set({ players: [...state.players, newPlayer] });
  },

  selectCharacter: (playerId, characterId) => {
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, characterId } : p
      ),
    }));
  },

  assignIdentities: () => {
    const state = get();
    // Fisher-Yates 洗牌（替代 Math.random 偏差版 sort）
    const shuffled = [...IDENTITIES];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const players = state.players.map((p, i) => ({
      ...p,
      identityId: shuffled[i % shuffled.length].id,
    }));
    set({ players });
  },

  startGame: () => {
    const state = get();
    // Give each player 1 random reverse card to start
    const allCardIds = REVERSE_CARDS.map(c => c.id as ReverseCardId);
    const players = state.players.map(p => ({
      ...p,
      reverseCards: [allCardIds[Math.floor(Math.random() * allCardIds.length)]],
    }));
    set({
      phase: 'playing',
      turnPhase: 'rolling',
      turnNumber: 1,
      roundNumber: 1,
      currentPlayerIndex: 0,
      players,
    });
  },

  resetGame: () => {
    set(createInitialState());
  },

  setTurnPhase: (phase) => set({ turnPhase: phase }),
  setPhase: (phase) => set({ phase }),
  setWinner: (winnerId) => set({ phase: 'gameOver', winnerId }),
  setDiceResult: (result) => set({ diceResult: result }),

  getCurrentPlayer: () => {
    const { players, currentPlayerIndex } = get();
    return players[currentPlayerIndex];
  },

  getCellById: (cellId) => {
    return get().board[cellId];
  },

  getPlayerCell: (playerId) => {
    const { players, board } = get();
    const player = players.find(p => p.id === playerId);
    if (!player) return board[0];
    return board[player.position];
  },

  // --- 骰子 ---
  rollDiceAction: () => {
    const player = get().getCurrentPlayer();
    const char = CHARACTERS.find(c => c.id === player.characterId);

    // 精准导航：使用预设步数
    if (player.preciseSteps !== null && player.preciseSteps > 0) {
      const steps = player.preciseSteps;
      const result: [number, number] = [steps, 0];
      set(state => ({
        diceResult: result,
        players: state.players.map(p =>
          p.id === player.id ? { ...p, lastDice: result, preciseSteps: null } : p
        ),
      }));
      return result;
    }

    let result: [number, number];
    if (char?.id === 'rabbit') {
      // 兔子赌徒：双骰二选一（不相加，由玩家选择）
      const d1 = Math.floor(Math.random() * 6) + 1;
      const d2 = Math.floor(Math.random() * 6) + 1;
      const high = Math.max(d1, d2);
      const low = Math.min(d1, d2);
      set(state => ({
        _diceOptionHigh: high,
        _diceOptionLow: low,
        diceResult: [d1, d2],
        turnPhase: 'diceChoice',
        players: state.players.map(p =>
          p.id === player.id ? { ...p, lastDice: [d1, d2] as [number, number] } : p
        ),
      }));
      return [d1, d2]; // 返回值仅用于显示，不用于移动
    } else {
      result = rollDice();
    }

    set(state => ({
      diceResult: result,
      players: state.players.map(p =>
        p.id === player.id ? { ...p, lastDice: result } : p
      ),
    }));

    return result;
  },

  // --- 移动 ---
  movePlayer: (playerId, steps) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return 0;

    const oldPos = player.position;
    let newPos = player.reverseWalk
      ? (oldPos - steps + 40) % 40
      : (oldPos + steps) % 40;

    // 检查是否经过起点
    const passedStart = player.reverseWalk
      ? false // 逆向行走不触发经过起点
      : (oldPos + steps >= 40);

    const char = CHARACTERS.find(c => c.id === player.characterId);
    let bonus = 0;
    if (passedStart) {
      bonus = 200 + (char?.id === 'zhaocaimao' ? 50 : 0);
      // 绝境反击：地产为0额外100
      if (player.properties.length === 0) {
        bonus += 100;
      }
    }

    set(state => ({
      players: state.players.map(p => {
        if (p.id !== playerId) return p;
        return {
          ...p,
          previousPosition: p.position,
          position: newPos,
          money: p.money + bonus,
          reverseWalk: false, // 仅持续一回合
        };
      }),
    }));

    if (bonus > 0) {
      get().addLog(playerId, `经过起点，获得${bonus}金币`, 'move');
    }

    return newPos;
  },

  // --- 地产 ---
  buyProperty: (playerId, cellId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    const cell = state.board[cellId];
    if (!player || !cell || cell.type !== 'property' || cell.price === undefined) return false;

    const char = CHARACTERS.find(c => c.id === player.characterId);
    let price = cell.price;
    if (char?.id === 'fox') price = Math.round(price * 0.9); // 狐狸商人9折

    // 全局事件价格修正
    if (state.activeGlobalEvent?.event.id === 'economic_winter') {
      price = Math.round(price * 0.8); // 经济寒冬：地产8折
    } else if (state.activeGlobalEvent?.event.id === 'golden_age') {
      price = Math.round(price * 1.2); // 黄金时代：地产1.2倍
    }

    if (player.money < price) return false;

    const newProperty: PropertyOwnership = {
      cellId,
      buildingLevel: 0,
      ownerId: playerId,
    };

    set({
      players: state.players.map(p =>
        p.id === playerId
          ? { ...p, money: p.money - price, properties: [...p.properties, cellId] }
          : p
      ),
      properties: [...state.properties, newProperty],
    });

    get().addLog(playerId, `购买了${cell.name}(${cell.street})，花费${price}金币`, 'buy');
    return true;
  },

  upgradeProperty: (playerId, cellId) => {
    const state = get();
    const prop = state.properties.find(p => p.cellId === cellId && p.ownerId === playerId);
    const cell = state.board[cellId];
    if (!prop || !cell || cell.upgradeCost === undefined || prop.buildingLevel >= 3) return false;

    let upgradeCost = cell.upgradeCost;
    // 全局事件价格修正
    if (state.activeGlobalEvent?.event.id === 'economic_winter') {
      upgradeCost = Math.round(upgradeCost * 0.8);
    } else if (state.activeGlobalEvent?.event.id === 'golden_age') {
      upgradeCost = Math.round(upgradeCost * 1.2);
    }

    const player = state.players.find(p => p.id === playerId);
    if (!player || player.money < upgradeCost) return false;

    const levelNames = ['空地', '房子', '别墅', '酒店'];

    set({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, money: p.money - upgradeCost } : p
      ),
      properties: state.properties.map(p =>
        p.cellId === cellId && p.ownerId === playerId
          ? { ...p, buildingLevel: (p.buildingLevel + 1) as 0 | 1 | 2 | 3 }
          : p
      ),
    });

    const newLevel = prop.buildingLevel + 1;
    get().addLog(playerId, `将${cell.name}升级为${levelNames[newLevel]}，花费${upgradeCost}金币`, 'buy');
    return true;
  },

  payRent: (fromId, toId, cellId) => {
    const state = get();
    const prop = state.properties.find(p => p.cellId === cellId && p.ownerId === toId);
    const cell = state.board[cellId];
    if (!prop || !cell || !cell.rentTable) return 0;

    // 检查联盟
    const fromPlayer = state.players.find(p => p.id === fromId);
    if (fromPlayer?.allianceWith === toId) return 0;

    let rent = cell.rentTable[prop.buildingLevel];

    // 乌龟理财师：租金+10%
    const toPlayer = state.players.find(p => p.id === toId);
    const toChar = CHARACTERS.find(c => c.id === toPlayer?.characterId);
    if (toChar?.id === 'turtle') rent = Math.round(rent * 1.1);

    // 野狼队长：联盟时租金减半（如果from是联盟成员）
    const fromChar = CHARACTERS.find(c => c.id === fromPlayer?.characterId);
    const allianceWith = state.players.find(p => p.id === fromPlayer?.allianceWith);
    if (allianceWith && fromChar?.id === 'wolf') rent = Math.round(rent / 2);

    // 全局事件影响
    if (state.activeGlobalEvent?.event.id === 'economic_winter') {
      rent = Math.round(rent / 2);
    } else if (state.activeGlobalEvent?.event.id === 'golden_age') {
      rent = Math.round(rent * 1.5);
    }

    // 守财奴免疫
    if ((fromPlayer?.immuneTurns ?? 0) > 0) {
      get().addLog(fromId, '守财奴免疫触发，免付租金', 'system');
      addToast(`🛡️ 守财奴免疫：免付租金 ¥${rent}`, 'info');
      return 0;
    }

    const actual = Math.min(rent, fromPlayer?.money || 0);

    set({
      players: state.players.map(p => {
        if (p.id === fromId) return { ...p, money: p.money - actual };
        if (p.id === toId) return { ...p, money: p.money + actual };
        return p;
      }),
    });

    if (actual > 0) {
      get().addLog(fromId, `向${toPlayer?.name || '玩家'}支付租金${actual}金币`, 'rent');
    }

    return actual;
  },

  // --- 卡牌 ---
  addReverseCard: (playerId, cardId) => {
    set(state => ({
      players: state.players.map(p => {
        if (p.id !== playerId) return p;
        if (p.reverseCards.length >= 3) return p; // 持有上限3张
        return { ...p, reverseCards: [...p.reverseCards, cardId] };
      }),
    }));
  },

  removeReverseCard: (playerId, cardId) => {
    set(state => ({
      players: state.players.map(p => {
        if (p.id !== playerId) return p;
        const idx = p.reverseCards.indexOf(cardId);
        if (idx === -1) return p;
        const cards = [...p.reverseCards];
        cards.splice(idx, 1);
        return { ...p, reverseCards: cards };
      }),
    }));
  },

  drawFateCard: (playerId) => {
    const state = get();
    let deck = [...state._fateDeck];
    if (deck.length === 0) {
      deck = shuffle(FATE_CARDS);
    }
    const card = deck.pop()!;

    set({
      _fateDeck: deck,
      players: state.players.map(p =>
        p.id === playerId
          ? { ...p, fateCardHistory: [...p.fateCardHistory, card.id] }
          : p
      ),
    });

    get().addLog(playerId, `抽到命运卡：${card.name}`, 'card');

    // Apply the card effect (pass get() snapshot including actions, not the get function)
    applyFateCard(get() as any, set, playerId, card);
    return card;
  },

  shuffleFateCards: () => {
    set({ _fateDeck: shuffle(FATE_CARDS) });
  },

  // --- 身份 ---
  revealIdentity: (playerId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.identityRevealed) return;

    const identity = IDENTITIES.find(i => i.id === player.identityId);
    if (!identity) return;

    // 应用身份暴露效果（与服务端 executeRevealIdentity 保持一致）
    const updates: Partial<Player> = { identityRevealed: true };
    let effectMsg = '';

    switch (identity.id) {
      case 'undercover_rich': {
        const bonus = Math.round(player.money * 0.3);
        updates.money = player.money + bonus;
        effectMsg = `获得 ¥${bonus}`;
        break;
      }
      case 'poor_god': {
        updates.money = player.money + 500;
        effectMsg = '获得 ¥500';
        break;
      }
      case 'miser': {
        updates.immuneTurns = 3;
        effectMsg = '获得 3 回合免疫';
        break;
      }
      case 'time_traveler': {
        updates.money = player.money + 500;
        updates.position = 0;
        updates.previousPosition = 0;
        effectMsg = '获得 ¥500 并传送回起点';
        break;
      }
    }

    set(state => ({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, ...updates } : p
      ),
    }));

    if (effectMsg) {
      get().addLog(playerId, `暴露身份「${identity.name}」：${effectMsg}`, 'identity');
    } else {
      get().addLog(playerId, `暴露了隐藏身份「${identity.name}」`, 'identity');
    }
  },

  // --- 经济 ---
  addMoney: (playerId, amount) => {
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, money: p.money + amount } : p
      ),
    }));
  },

  deductMoney: (playerId, amount) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return 0;
    const actual = Math.min(amount, player.money);
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, money: p.money - actual } : p
      ),
    }));
    return actual;
  },

  payTax: (playerId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return 0;
    if (player.immuneTurns > 0) {
      get().addLog(playerId, '守财奴免疫触发，免缴税金', 'system');
      addToast('🛡️ 守财奴免疫：免缴税金', 'info');
      return 0;
    }
    const fixedTax = 200;
    const percentTax = Math.round(player.money * 0.1);
    const tax = Math.min(fixedTax, percentTax);
    get().deductMoney(playerId, tax);
    get().addLog(playerId, `缴纳税金${tax}金币`, 'system');
    return tax;
  },

  // --- 监狱 ---
  sendToJail: (playerId) => {
    const char = get().players.find(p => p.id === playerId)
      ? CHARACTERS.find(c => c.id === get().players.find(p => p.id === playerId)!.characterId)
      : null;
    if (char?.id === 'dragon') {
      get().addLog(playerId, '小龙律师使用法务豁免，免于入狱', 'system');
      return;
    }
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId
          ? { ...p, inJail: true, jailTurns: 1, position: 21 }
          : p
      ),
    }));
    get().addLog(playerId, '被送入监狱', 'system');
  },

  payBail: (playerId) => {
    get().deductMoney(playerId, 200);
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, inJail: false, jailTurns: 0 } : p
      ),
    }));
    get().addLog(playerId, '支付200保释金出狱', 'system');
  },

  waitInJail: (playerId) => {
    set(state => ({
      players: state.players.map(p => {
        if (p.id !== playerId) return p;
        const newTurns = p.jailTurns - 1;
        return {
          ...p,
          jailTurns: newTurns,
          inJail: newTurns > 0,
        };
      }),
    }));
  },

  freeFromJail: (playerId) => {
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId ? { ...p, inJail: false, jailTurns: 0 } : p
      ),
    }));
    get().addLog(playerId, '掷出双数成功出狱', 'system');
  },

  // --- 全局事件 ---
  checkAndTriggerGlobalEvent: (force = false, silent = false) => {
    const state = get();
    const shouldFire = force || shouldTriggerGlobalEvent(state.roundNumber);
    if (!shouldFire) return null;

    if (force) forceTriggerGlobalEvent(state.roundNumber);

    const event = getGlobalEvent();
    if (!event) return null;

    // 嘉年华是即时效果，不需要 activeGlobalEvent
    if (event.id === 'carnival_event') {
      applyGlobalEvent(get() as any, event);
    } else {
      set({ activeGlobalEvent: { event, remainingTurns: event.duration } });
    }

    get().addLog('system', `全局事件：${event.emoji} ${event.name}`, 'event');
    // 静默模式：不弹出 Broadcast（EventModal 已经显示了相同内容）
    if (!silent) {
      get().setBroadcast(`⚡ ${event.emoji} ${event.name} — ${event.description}`, 'warning');
    }

    return { event, remainingTurns: event.duration };
  },

  // --- 破产 ---
  checkBankruptcy: (playerId) => {
    const player = get().players.find(p => p.id === playerId);
    return player ? player.money <= 0 : false;
  },

  handleBankruptcy: (playerId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;

    // 慈善家：免死金牌
    const identity = IDENTITIES.find(i => i.id === player.identityId);
    if (identity?.id === 'miser' && player.identityRevealed) {
      // 保留一座最贵地产
      const mostExpensive = player.properties
        .map(pid => ({ id: pid, price: state.board[pid]?.price || 0 }))
        .sort((a, b) => b.price - a.price)[0];

      const keepProperty = mostExpensive?.id;
      const lostProperties = player.properties.filter(pid => pid !== keepProperty);

      set({
        players: state.players.map(p => {
          if (p.id !== playerId) return p;
          return {
            ...p,
            properties: keepProperty ? [keepProperty] : [],
            money: 300, // 续命资金
            bankrupt: false,
          };
        }),
        properties: state.properties.filter(p => !lostProperties.includes(p.cellId)),
      });

      get().addLog(playerId, '慈善家免死金牌触发！保留最贵地产，获得300续命金', 'identity');
      return;
    }

    // 普通破产 — 同时清理联盟关系
    const bankruptPlayer = state.players.find(p => p.id === playerId);
    const alliancePartner = bankruptPlayer?.allianceWith;
    set({
      players: state.players.map(p => {
        if (p.id === playerId) return { ...p, bankrupt: true, money: 0, properties: [], allianceWith: null, allianceTurns: 0 };
        // 如果别的玩家与被破产者联盟，也清理
        if (p.allianceWith === playerId) return { ...p, allianceWith: null, allianceTurns: 0 };
        return p;
      }),
      properties: state.properties.filter(p => p.ownerId !== playerId),
    });

    if (alliancePartner) {
      const partner = state.players.find(p => p.id === alliancePartner);
      get().addLog('system', `${bankruptPlayer?.name} 破产，与 ${partner?.name || alliancePartner} 的联盟解除`, 'alliance');
    }

    get().addLog(playerId, '破产了！', 'bankrupt');

    // 选出第一个地产格子做闪烁高亮
    const bankruptCellId = bankruptPlayer?.properties?.[0];
    if (bankruptCellId !== undefined) {
      const bc = state.board[bankruptCellId];
      get().notifyLandChange(bankruptCellId, 'bankrupt',
        `「${bc?.name || '未知地产'}」${bankruptPlayer?.name || '玩家'}破产，所有地产已清空`);
    }

    // 检查胜利条件
    const activePlayers = get().players.filter(p => !p.bankrupt);
    if (activePlayers.length <= 1) {
      get().setWinner(activePlayers[0]?.id || '');
      return; // 游戏结束，不需要推进回合
    }

    // 推进到下一位玩家
    get().nextTurn();
  },

  // --- 破产清算 ---
  startBankruptcyLiquidation: (playerId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.bankrupt) return;

    // 检查守财奴免死金牌
    const identity = IDENTITIES.find(i => i.id === player.identityId);
    if (identity?.id === 'miser' && player.identityRevealed) {
      get().handleBankruptcy(playerId);
      return;
    }

    // 如果没有地产，直接破产
    if (!player.properties || player.properties.length === 0) {
      get().handleBankruptcy(playerId);
      return;
    }

    // Bot：自动变卖最便宜的地产，直到现金>0 或没有地产
    if (player.isBot) {
      get().addLog(playerId, `${player.name} 进入自动清算...`, 'bankrupt');
      let currentMoney = player.money;
      const propertiesToSell: number[] = [];
      const propertiesSorted: { id: number; price: number }[] = [...player.properties]
        .map(pid => ({ id: pid, price: BOARD_CELLS[pid]?.price || 0 }))
        .sort((a, b) => a.price - b.price);

      for (const prop of propertiesSorted) {
        if (currentMoney > 0) break;
        currentMoney += prop.price;
        propertiesToSell.push(prop.id);
      }

      if (propertiesToSell.length === 0 || currentMoney <= 0) {
        // 卖完所有地还不够 → 真正破产
        get().confirmBankruptcy();
        return;
      }

      // 一次性更新状态
      set({
        players: state.players.map(p => {
          if (p.id !== playerId) return p;
          return {
            ...p,
            money: currentMoney,
            properties: p.properties.filter(id => !propertiesToSell.includes(id)),
          };
        }),
        properties: state.properties.filter(p => !(propertiesToSell.includes(p.cellId) && p.ownerId === playerId)),
      });

      get().addLog(playerId, `自动清算：变卖 ${propertiesToSell.length} 块地产，获得 ¥${currentMoney - player.money}`, 'bankrupt');

      // 通知第一个地产的格子做闪烁
      if (propertiesToSell.length > 0) {
        const firstCell = state.board[propertiesToSell[0]];
        get().notifyLandChange(propertiesToSell[0], 'bankrupt',
          `${player.name} 清算变卖 ${propertiesToSell.length} 块地产（含「${firstCell?.name || '未知'}」等），获得 ¥${currentMoney - player.money}`);
      }

      // 继续游戏
      const s = get();
      s.checkComeback(playerId);
      s.nextTurn();
      return;
    }

    set({ showBankruptcyLiquidation: true, pendingBankruptPlayerId: playerId });
    get().addLog(playerId, '资产不足，进入破产清算流程！', 'bankrupt');
  },

  cancelBankruptcyLiquidation: () => {
    set({ showBankruptcyLiquidation: false, pendingBankruptPlayerId: null });
  },

  confirmBankruptcy: () => {
    const state = get();
    const playerId = state.pendingBankruptPlayerId;
    set({ showBankruptcyLiquidation: false, pendingBankruptPlayerId: null });
    if (playerId) {
      get().handleBankruptcy(playerId);
    }
  },

  sellPropertyForLiquidation: (playerId, cellId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    const cell = state.board[cellId];
    if (!player || !cell) return;

    const price = cell.price || 0;

    // 一次性完成状态更新（更安全）
    set({
      players: state.players.map(p => {
        if (p.id !== playerId) return p;
        return {
          ...p,
          money: p.money + price,
          properties: p.properties.filter(id => id !== cellId),
        };
      }),
      properties: state.properties.filter(p => !(p.cellId === cellId && p.ownerId === playerId)),
    });

    get().addLog(playerId, `清算：变卖 ${cell.name}，获得 ¥${price}`, 'bankrupt');
    get().notifyLandChange(cellId, 'bankrupt', `「${cell.name}」破产变卖，获得 ¥${price}`);

    // 如果现金 > 0，关闭清算面板，延迟一帧继续游戏（让 React 完成卸载）
    const updatedPlayer = get().players.find(p => p.id === playerId);
    if (updatedPlayer && updatedPlayer.money > 0) {
      set({ showBankruptcyLiquidation: false, pendingBankruptPlayerId: null });
      setTimeout(() => {
        get().checkComeback(playerId);
        get().nextTurn();
      }, 0);
    }
  },

  // --- 通信 / 日志 ---
  setBroadcast: (message, type) => set({ broadcast: { message, type } }),

  clearBroadcast: () => set({ broadcast: null }),

  addLog: (playerId, message, type) => set(state => ({
    logs: [...state.logs, {
      turn: state.turnNumber,
      playerId,
      message,
      type,
      timestamp: Date.now(),
    }].slice(-50),
  })),

  nextTurn: () => {
    const state = get();

    // ====== 1. 联盟回合倒计时（按玩家个体回合递减） ======
    const allianceMap = new Map<string, { partner: string; turns: number }>();
    state.players.forEach(p => {
      if (p.allianceWith && p.allianceTurns > 0) {
        const key = [p.id, p.allianceWith].sort().join('-');
        if (!allianceMap.has(key)) {
          allianceMap.set(key, { partner: p.allianceWith, turns: p.allianceTurns });
        }
      }
    });

    const newAllianceTurns = new Map<string, number>();
    allianceMap.forEach((v, key) => {
      const newTurns = v.turns - 1;
      if (newTurns <= 0) {
        const [aId, bId] = key.split('-');
        const a = state.players.find(p => p.id === aId);
        const b = state.players.find(p => p.id === bId);
        if (a && b) {
          get().addLog('system', `${a.name} 与 ${b.name} 的联盟到期`, 'alliance');
          get().setBroadcast(`🤝 ${a.name} 与 ${b.name} 的联盟解除`, 'info');
        }
      }
      newAllianceTurns.set(key, newTurns);
    });

    // ====== 2. 免疫回合倒计时（仅当前回合结束的玩家） ======
    // ====== 3. 更新状态 + 递增 turnNumber ======
    set({
      players: state.players.map(p => {
        let updated = { ...p };

        // 处理联盟过期
        if (p.allianceWith) {
          const key = [p.id, p.allianceWith].sort().join('-');
          const remaining = newAllianceTurns.get(key);
          if (remaining !== undefined && remaining <= 0) {
            updated.allianceWith = null;
            updated.allianceTurns = 0;
          } else if (remaining !== undefined) {
            updated.allianceTurns = remaining;
          }
        }

        // 处理免疫回合（仅当前玩家减少）
        if (p.immuneTurns > 0 && p.id === state.players[state.currentPlayerIndex]?.id) {
          updated.immuneTurns = p.immuneTurns - 1;
          if (updated.immuneTurns <= 0) {
            get().addLog(p.id, '守财奴免疫效果结束', 'system');
          }
        }

        return updated;
      }),
      turnNumber: state.turnNumber + 1,
    });

    // ====== 4. 推进到下一位玩家（检测轮次变化 + 全局事件） ======
    get().nextPlayer();
  },

  nextPlayer: () => {
    const state = get();
    let next = (state.currentPlayerIndex + 1) % state.players.length;
    let tries = 0;
    while (state.players[next].bankrupt && tries < state.players.length) {
      next = (next + 1) % state.players.length;
      tries++;
    }
    const activePlayers = state.players.filter(p => !p.bankrupt);
    if (activePlayers.length <= 1) {
      set({ winnerId: activePlayers[0]?.id || '', phase: 'gameOver' });
      return;
    }

    // ====== 检测是否进入新轮（索引回绕到第一位或更小 = 完成一圈） ======
    const isNewRound = next <= state.currentPlayerIndex;

    let newRoundNumber = state.roundNumber;
    let newActiveGlobalEvent = state.activeGlobalEvent;

    if (isNewRound) {
      newRoundNumber = state.roundNumber + 1;

      // 全局事件倒计时（按轮递减）
      if (state.activeGlobalEvent) {
        const remaining = state.activeGlobalEvent.remainingTurns - 1;
        if (remaining <= 0) {
          const endedName = state.activeGlobalEvent.event.name;
          newActiveGlobalEvent = null;
          // 延迟一帧通知（避免 set 未生效时 get() 读到旧 state）
          setTimeout(() => {
            const s = get();
            s.addLog('system', `全局事件「${endedName}」已结束`, 'event');
            s.setBroadcast(`「${endedName}」已结束，恢复正常`, 'info');
          }, 0);
        } else {
          newActiveGlobalEvent = { ...state.activeGlobalEvent, remainingTurns: remaining };
        }
      }
    }

    set({
      currentPlayerIndex: next,
      roundNumber: newRoundNumber,
      activeGlobalEvent: newActiveGlobalEvent,
      // 所有玩家回合开始都进入 rolling：Bot 自动执行，人类可点击掷骰子
      turnPhase: 'rolling',
      diceResult: null,
    });

    // ====== 新轮开始：检查触发全局事件 ======
    if (isNewRound) {
      // 延迟触发以避免 get() 读到旧 state
      setTimeout(() => get().checkAndTriggerGlobalEvent(), 0);
    }
  },

  // --- 绝境反击 ---
  checkComeback: (playerId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.bankrupt) return;

    const activePlayers = state.players.filter(p => !p.bankrupt);
    const sortedByMoney = [...activePlayers].sort((a, b) => b.money - a.money);
    const rank = sortedByMoney.findIndex(p => p.id === playerId) + 1;
    const isLast = rank === sortedByMoney.length;
    const richest = sortedByMoney[0];
    const comebackAvailable = state.roundNumber - state.lastComeback >= 1; // 每轮最多触发一次

    let notification: { id: number; emoji: string; title: string; description: string; } | null = null;

    // 条件A：资产排名倒数第一，每轮获得1张随机反转卡
    if (isLast && comebackAvailable && player.reverseCards.length < 3) {
      const cardIds: ReverseCardId[] = ['乾坤一掷', '一夜暴富', '拆迁令', '时光倒流', '精准导航', '强制联盟'];
      const randomCard = cardIds[Math.floor(Math.random() * cardIds.length)];
      get().addReverseCard(playerId, randomCard);
      set({ lastComeback: state.roundNumber });
      get().addLog(playerId, '绝境反击！获得反转卡（资产排名末位补偿）', 'system');
      notification = {
        id: Date.now(),
        emoji: '🔥',
        title: '绝境反击！',
        description: `资产排名末位补偿 —— 获得反转卡「${randomCard}」`,
      };
    }

    // 条件B：资产不足最富玩家30%，给小额现金补偿
    if (richest && comebackAvailable && player.money < richest.money * 0.3 && player.money > 0) {
      const bonus = Math.min(Math.floor((richest.money - player.money) * 0.1), 500);
      get().addMoney(playerId, bonus);
      set({ lastComeback: state.roundNumber });
      get().addLog(playerId, `绝境反击！获得补偿金 ¥${bonus}`, 'system');
      notification = {
        id: Date.now(),
        emoji: '💰',
        title: '绝境反击！',
        description: `资产不足富豪30% —— 获得补偿金 ¥${bonus}`,
      };
    }

    // 有通知就设置（GameBoard 监听并弹窗）
    if (notification) {
      set({ comebackNotification: notification });
    }
  },

  // --- 地产变动通知 ---
  notifyLandChange: (cellId, landType, extraMessage) => {
    const state = get();
    const cell = state.board[cellId];
    if (!cell) return;

    const typeLabels: Record<string, string> = {
      lost: '💔 失去地产',
      downgraded: '⬇️ 地产降级',
      stolen: '👻 地产被夺',
      swapped: '🔄 地产交换',
      bankrupt: '💸 破产清算',
    };

    const label = typeLabels[landType] || '📋 地产变动';
    const message = extraMessage || `「${cell.name}」${landType === 'downgraded' ? '被降级' : landType === 'stolen' ? '被夺走' : landType === 'swapped' ? '已交换' : '已失去'}`;

    set({
      landChangeNotification: {
        id: Date.now(),
        cellId,
        cellName: cell.name,
        landType,
        message: `${label} — ${message}`,
      },
    });
  },

  // ===== 角色技能 =====

  // 鹰眼投资人：预览下3张命运卡（不消耗）
  getFateDeckPreview: () => {
    const deck = get()._fateDeck;
    return deck.slice(-3).reverse();
  },

  // 兔子赌徒：确认选择的步数
  chooseDiceSteps: (steps: number) => {
    const state = get();
    const cp = state.players[state.currentPlayerIndex];
    if (!cp || cp.characterId !== 'rabbit') return;

    // 计算新位置（自己处理，不依赖 movePlayer 的异步 set）
    const oldPos = cp.position;
    const newPos = cp.reverseWalk
      ? (oldPos - steps + 40) % 40
      : (oldPos + steps) % 40;
    const passedStart = !cp.reverseWalk && (oldPos + steps >= 40);
    const char = CHARACTERS.find(c => c.id === cp.characterId);
    let bonus = 0;
    if (passedStart) {
      bonus = 200 + (char?.id === 'zhaocaimao' ? 50 : 0);
      if (cp.properties.length === 0) bonus += 100;
    }

    // 一次 set 原子更新所有字段，无竞态
    set(s => ({
      turnPhase: 'moving',
      _diceOptionHigh: null,
      _diceOptionLow: null,
      diceResult: [steps, 0] as [number, number],
      players: s.players.map(p => {
        if (p.id !== cp.id) return p;
        return {
          ...p,
          previousPosition: oldPos,
          position: newPos,
          money: p.money + bonus,
          reverseWalk: false,
          lastDice: [steps, 0] as [number, number],
        };
      }),
    }));

    if (bonus > 0) {
      get().addLog(cp.id, `经过起点，获得${bonus}金币`, 'move');
    }
  },

  // 猫头鹰学者：买地前预览命运卡（仅展示，不应用效果）
  owlPreviewFate: () => {
    const state = get();
    let deck = [...state._fateDeck];
    if (deck.length === 0) {
      deck = shuffle(FATE_CARDS);
    }
    // 只取最后一张作为"预览"，不消耗、不应用效果
    const card = deck[deck.length - 1];
    return card;
  },

  _restoreState: (playerId) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;
    set(state => ({
      players: state.players.map(p =>
        p.id === playerId
          ? { ...p, position: p.previousPosition }
          : p
      ),
    }));
  },
}));
